import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VerifiedEmailBadge from '../ui/VerifiedEmailBadge';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

function statusBadgeClass(status) {
  if (status === 'verified') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'pending') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-surface-muted text-ink-muted border-edge';
}

export default function SchoolAffiliationPanel({
  affiliationStatus = 'none',
  affiliationMethod = null,
  schoolName = '',
  userEmail = '',
  userRole = 'Stu',
  onUpdated,
  compact = false
}) {
  const { t } = useTranslation();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isStudent = userRole === 'Stu';
  const isUnaffiliated = affiliationStatus !== 'verified';

  const handleVerifyEmail = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/affiliations/verify-email', {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('school.verifyEmailFailed'));
      setMessage(t('school.verifyEmailSuccess'));
      onUpdated?.(data.user);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/affiliations/accept', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ invite_code: inviteCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('school.acceptInviteFailed'));
      setMessage(t('school.affiliationAccepted'));
      setInviteCode('');
      onUpdated?.(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = t(`school.affiliationStatus.${affiliationStatus}`, { defaultValue: affiliationStatus });
  const methodLabel = affiliationMethod
    ? t(`school.affiliationMethod.${affiliationMethod}`, { defaultValue: affiliationMethod })
    : null;

  if (compact) {
    return (
      <div className="bg-surface-muted border border-edge p-4 rounded-lg sm:col-span-2 lg:col-span-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-ink-subtle text-sm">{t('school.affiliationStatusLabel')}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass(affiliationStatus)}`}>
                {statusLabel}
              </span>
              {affiliationStatus === 'verified' && schoolName ? (
                <span className="text-sm font-semibold text-ink">{schoolName}</span>
              ) : null}
              {methodLabel ? (
                <span className="text-xs text-ink-subtle">({methodLabel})</span>
              ) : null}
              <VerifiedEmailBadge
                affiliationStatus={affiliationStatus}
                affiliationMethod={affiliationMethod}
              />
            </div>
            {userEmail && affiliationStatus === 'verified' && affiliationMethod === 'email' ? (
              <p className="mt-1 text-sm text-ink-muted break-all">{userEmail}</p>
            ) : null}
          </div>
          {isUnaffiliated ? (
            <Link
              to="/edit-profile"
              className="text-sm font-medium text-link hover:text-link-hover whitespace-nowrap"
            >
              {t('school.linkSchoolAccount')}
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-edge bg-surface-muted p-4 space-y-4">
      <div>
        <h4 className="text-sm font-medium text-ink">{t('school.affiliationStatusLabel')}</h4>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass(affiliationStatus)}`}>
            {statusLabel}
          </span>
          {affiliationStatus === 'verified' && schoolName ? (
            <span className="text-sm font-semibold text-ink">{schoolName}</span>
          ) : null}
          {methodLabel ? (
            <span className="text-xs text-ink-subtle">({methodLabel})</span>
          ) : null}
          <VerifiedEmailBadge
            affiliationStatus={affiliationStatus}
            affiliationMethod={affiliationMethod}
          />
        </div>
        {affiliationStatus === 'none' ? (
          <p className="mt-2 text-sm text-ink-muted">{t('school.affiliationNoneHelp')}</p>
        ) : null}
      </div>

      {error ? <p className="text-sm text-danger" role="alert">{error}</p> : null}
      {message ? <p className="text-sm text-success" role="status">{message}</p> : null}

      {isUnaffiliated ? (
        <div className="space-y-4 border-t border-edge pt-4">
          {isStudent ? (
            <div>
              <p className="text-sm font-medium text-ink-muted">{t('school.verifyEmailTitle')}</p>
              <p className="text-xs text-ink-subtle mt-1">{t('school.verifyEmailHelp')}</p>
              <button
                type="button"
                onClick={handleVerifyEmail}
                disabled={loading}
                className="mt-2 px-3 py-2 text-sm font-medium rounded-full bg-surface border border-edge-strong text-ink hover:bg-surface-muted disabled:opacity-50"
              >
                {loading ? t('common.loading') : t('school.verifyEmailAction')}
              </button>
            </div>
          ) : null}

          <form onSubmit={handleAcceptInvite}>
            <p className="text-sm font-medium text-ink-muted">{t('school.acceptInviteTitle')}</p>
            <p className="text-xs text-ink-subtle mt-1">{t('school.acceptInviteHelp')}</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="EDV-XXXX"
                className="flex-1 border border-edge-strong bg-surface text-ink rounded-md px-3 py-2 text-sm font-mono uppercase"
              />
              <button
                type="submit"
                disabled={loading || !inviteCode.trim()}
                className="px-4 py-2 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                {loading ? t('common.loading') : t('school.acceptInvite')}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
